import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { usePageMeta, PageHero, Eyebrow } from "../components/Shared";
import { COMPANY, IMAGES } from "../data/site";

const inputCls =
  "w-full bg-white border border-border px-4 py-3 text-charcoal placeholder:text-gray-400 focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/30 rounded-none";

const MAP_SRC =
  "https://www.google.com/maps?q=1750+Britannia+Rd+E,+Mississauga,+ON+L4W+1J3&output=embed";

export default function Contact() {
  usePageMeta("Contact", "Contact CoreDock Logistics in Mississauga. Call +1 647-204-6323 or email info@coredocklogistics.com.");
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, lead_type: "contact", phone: form.phone || "N/A" });
      setDone(true);
      toast.success("Message sent — we'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const info = [
    { icon: MapPin, label: "Address", value: `${COMPANY.name}, ${COMPANY.address}, ${COMPANY.city}` },
    { icon: Phone, label: "Phone", value: COMPANY.phone, href: `tel:${COMPANY.phoneRaw}`, testid: "contact-phone" },
    { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}`, testid: "contact-email" },
    { icon: Clock, label: "Business Hours", value: COMPANY.hours },
  ];

  return (
    <>
      <PageHero eyebrow="Get in Touch" image={IMAGES.docksBay}
        title="Let's Move Your Freight"
        subtitle="Reach our team directly, or send a message and we'll respond with a practical solution." />

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <Eyebrow>Contact Details</Eyebrow>
            <div className="mt-6 space-y-4">
              {info.map((x) => (
                <div key={x.label} className="flex gap-4 bg-white border border-border p-5" data-testid={x.testid}>
                  <div className="w-11 h-11 bg-navy text-orange flex items-center justify-center shrink-0">
                    <x.icon size={20} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="tag-eyebrow">{x.label}</p>
                    {x.href
                      ? <a href={x.href} className="font-display font-semibold text-lg link-underline">{x.value}</a>
                      : <p className="font-display font-semibold text-lg">{x.value}</p>}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 border-4 border-navy overflow-hidden h-[280px]">
              <iframe title="CoreDock Logistics map" src={MAP_SRC} className="w-full h-full border-0"
                      loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="contact-map" />
            </div>
          </div>

          <div>
            <Eyebrow>Send a Message</Eyebrow>
            {done ? (
              <div className="mt-6 bg-white border border-border p-10 text-center" data-testid="contact-success">
                <CheckCircle2 size={56} className="text-orange mx-auto" strokeWidth={1.5} />
                <h3 className="font-display font-bold text-2xl mt-5">Thank you for contacting CoreDock Logistics.</h3>
                <p className="mt-3 text-text-secondary">We have received your request and will contact you shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="mt-6 bg-white border border-border p-6 md:p-8 space-y-5">
                <input type="text" value={form.website} onChange={set("website")} tabIndex={-1} className="hidden" aria-hidden="true" />
                <div>
                  <span className="text-sm font-semibold text-navy">Full Name <span className="text-orange">*</span></span>
                  <input data-testid="c-name" className={`${inputCls} mt-1.5`} value={form.full_name} onChange={set("full_name")} placeholder="Jane Smith" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <span className="text-sm font-semibold text-navy">Email <span className="text-orange">*</span></span>
                    <input data-testid="c-email" type="email" className={`${inputCls} mt-1.5`} value={form.email} onChange={set("email")} placeholder="jane@company.com" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-navy">Phone</span>
                    <input data-testid="c-phone" className={`${inputCls} mt-1.5`} value={form.phone} onChange={set("phone")} placeholder="647-000-0000" />
                  </div>
                </div>
                <div>
                  <span className="text-sm font-semibold text-navy">Message <span className="text-orange">*</span></span>
                  <textarea data-testid="c-message" rows={6} className={`${inputCls} mt-1.5`} value={form.message} onChange={set("message")} placeholder="Tell us what you're moving, when, and where…" />
                </div>
                <button type="submit" data-testid="c-submit" disabled={submitting}
                        className="btn-orange w-full py-4 font-semibold uppercase tracking-wide flex items-center justify-center gap-2 disabled:opacity-60">
                  {submitting ? <><Loader2 className="animate-spin" size={18} /> Sending…</> : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
